import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../api/context";
import { useRealtimeConnectionState, useRealtimeEvent } from "../../lib/realtime";
import type { WalletInfo } from "../../api/types";
import { formatAmount } from "./format";
import { usePolling } from "../../lib/usePolling";

type State = { status: "loading" } | { status: "ready"; wallet: WalletInfo } | { status: "error" };

/**
 * Always-visible wallet balance in the header (FR-BIL-006), formatted in the workspace
 * currency (SAR 2 dp / BHD 3 dp). Links to Settings → Billing. While loading it keeps the
 * same box (no header layout shift); a failed load hides it rather than showing a wrong
 * number. Updates live from the `wallet.updated` WS event (WP-315); while the socket is not open it
 * re-polls the wallet every `WALLET_POLL_MS`.
 */
export const WALLET_POLL_MS = 30_000;

export function WalletChip({ workspaceId }: { workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const [state, setState] = useState<State>({ status: "loading" });
  const connection = useRealtimeConnectionState();

  useRealtimeEvent((envelope) => {
    if (envelope.event !== "wallet.updated") return;
    const { balanceMinor, currency } = envelope.payload as {
      balanceMinor: string;
      currency: string;
    };
    setState((prev) =>
      prev.status === "ready"
        ? { status: "ready", wallet: { ...prev.wallet, balanceMinor, currency } as WalletInfo }
        : prev,
    );
  });

  // Fallback: no live socket -> poll quietly (a failed poll keeps the last good number).
  usePolling(
    () =>
      api.getWallet(workspaceId).then(
        (wallet) => setState({ status: "ready", wallet }),
        () => undefined,
      ),
    WALLET_POLL_MS,
    { enabled: connection !== "open" },
  );

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    api.getWallet(workspaceId).then(
      (wallet) => {
        if (active) setState({ status: "ready", wallet });
      },
      () => {
        if (active) setState({ status: "error" });
      },
    );
    return () => {
      active = false;
    };
  }, [api, workspaceId]);

  if (state.status === "error") return null;
  const amount =
    state.status === "ready"
      ? formatAmount(state.wallet.balanceMinor, state.wallet.currency, locale)
      : null;
  return (
    <Link
      to={`/w/${workspaceId}/settings/billing`}
      aria-label={
        amount === null ? t("billing.chip.label") : t("billing.chip.ariaLabel", { amount })
      }
      aria-busy={amount === null}
      className="inline-flex h-8 items-center gap-2 rounded-full border border-line bg-surface-muted px-3 text-sm text-fg hover:bg-surface-sunken"
      data-testid="wallet-chip"
    >
      <span className="text-fg-muted">{t("billing.chip.label")}</span>
      <bdi className="font-semibold tabular-nums">{amount ?? "…"}</bdi>
    </Link>
  );
}
